import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import "../styles/community.css";


const insideCards = [
  {
    icon: "🌊",
    name: "Flow Journey Journal",
    desc: "Six months of daily guided practice to deepen and integrate everything from the Flow Journey. This is where the awareness stabilizes — and becomes a way of living, not just a six-week experience.",
  },
  {
    icon: "🗓",
    name: "Monthly Live Sessions",
    desc: "Monthly gatherings to continue learning and growing with each other. Facilitated by Alec and Stan — and increasingly shaped by the community itself. These aren't webinars. They're dialogues.",
  },
  {
    icon: "✍️",
    name: "Weekly Prompts",
    desc: "A weekly reflection prompt to keep awareness alive between sessions. Alec and Stan share their own personal reflections a few days after each prompt — modeling the practice they're inviting.",
  },
  {
    icon: "📚",
    name: "Full Flow Journey Materials",
    desc: "Complete access to the Flow Journey content — asynchronously, to revisit and go deeper on any stage of the journey at your own pace.",
  },
  {
    icon: "🤝",
    name: "Self-Organizing Groups",
    desc: "Members organize around shared causes, questions, and domains they care about changing. Climate. Education. Healthcare. Leadership. Whatever pulls them — they don't have to pull alone.",
  },
  {
    icon: "🌐",
    name: "A Global Network",
    desc: "Flow Journey alumni from around the world. Different backgrounds, sectors, and life stages — unified by having done the inner work and choosing to keep going.",
  },
];

const pricingItems = [
  "Flow Journey Journal — 6 months of daily guided practice",
  "Full access to all Flow Journey materials and discussion boards",
  "Monthly live sessions with Alec, Stan, and fellow community members",
  "Weekly reflection prompts with personal responses from Alec & Stan",
  "Access to self-organizing groups around shared causes",
  "A global network of Flow Journey alumni",
];

const cocreateExamples = [
  {
    icon: "🧭",
    label: "Leadership",
    desc: "Leaders who've done the inner work, reimagining how organizations navigate uncertainty together.",
  },
  {
    icon: "🌍",
    label: "Systems change",
    desc: "People from different sectors, finding the leverage points that only become visible from flow.",
  },
  {
    icon: "💡",
    label: "Innovation",
    desc: "New approaches to old problems — emerging not from pressure, but from presence.",
  },
];

// Shared fade-up variant
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};


export default function Community() {
  return (
    <>
      {/* ── HERO ── */}
      <section className="cm-hero">
        <p className="cm-hero-eyebrow">SI Flow Community</p>
        <h1>
          Where flow becomes<br />
          <em>something larger than you.</em>
        </h1>
        <p className="cm-hero-sub">
          The Flow Journey is where you find it within yourself. The SI Flow
          Community is where something extraordinary becomes possible — when
          people living from their Flow Identities come together with intention.
        </p>
        <div className="cm-hero-cta-group">
          <Link to="/community#pricing" className="btn-sand-cm">
            Join the community →
          </Link>
          <Link to="/flow-journey" className="btn-ghost-light-cm">
            Start with the Flow Journey
          </Link>
        </div>
      </section>

      {/* ── PEBBLE / BOULDER ── */}
      <motion.section
        className="cm-ripple-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="cm-ripple-inner">
          <p className="section-label">The vision</p>
          <h2 className="section-title">A pebble. Then a boulder.</h2>

          <div className="cm-ripple-grid">
            {/* Pebble card */}
            <div className="cm-ripple-card">
              <div className="cm-ripple-visual">
                <div className="cm-ripple-circle pebble">
                  <span className="cm-ripple-icon">🪨</span>
                </div>
              </div>
              <div className="cm-ripple-label">The Flow Journey</div>
              <div className="cm-ripple-desc">
                One person. Six weeks of inner work. The awareness, presence, and
                Flow Identity that ripples outward — into family, team, community.
              </div>
              <div className="cm-ripple-effects">
                {["Self", "Family", "Team", "Community"].map((tag) => (
                  <span className="cm-ripple-effect" key={tag}>{tag}</span>
                ))}
              </div>
            </div>

            <div className="cm-ripple-arrow">→</div>

            {/* Boulder card */}
            <div className="cm-ripple-card boulder">
              <div className="cm-ripple-visual">
                <div className="cm-ripple-circle big">
                  <span className="cm-ripple-icon" style={{ color: "white" }}>🌊</span>
                </div>
              </div>
              <div className="cm-ripple-label">SI Flow Community</div>
              <div className="cm-ripple-desc">
                Many people, operating from Flow Identities, together. The ripples
                are larger. They travel further. They reach places no individual
                pebble can.
              </div>
              <div className="cm-ripple-effects">
                {["Organizations", "Systems", "Society", "The world"].map((tag) => (
                  <span className="cm-ripple-effect" key={tag}>{tag}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="cm-ripple-bottom">
            <p>
              "This is our hypothesis — and our deepest conviction: that the world
              doesn't need more information or better strategy. It needs more people
              who have done the inner work, finding each other, and creating together
              from flow."
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── VISION ── */}
      <motion.section
        className="cm-vision"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="cm-vision-inner">
          <p className="section-label">Why this matters now</p>
          <h2 className="section-title">The world needs a different kind of change.</h2>
          <p className="cm-vision-body">
            Most approaches to social and systemic change — however well-intentioned
            — are built on the same fear-based patterns they're trying to transform.
            They move fast, react, compete for attention, and burn people out. The
            change that happens is often surface-level. The underlying dynamics remain.
          </p>
          <p className="cm-vision-body">
            <strong>Synergistic Intelligence begins from a different premise.</strong>{" "}
            When people who have genuinely moved beyond survival-based patterns come
            together — without ego defensiveness, without the need to be right,
            without the exhaustion of performing — something qualitatively different
            becomes possible. A kind of collective intelligence that no individual,
            however capable, can access alone.
          </p>
          <div className="cm-vision-pull">
            <p>
              "We believe real, lasting change ripples outward from the inside. The
              SI Flow Community is where we test that belief — together, openly, with
              humility about what we don't yet know."
            </p>
          </div>
          <p className="cm-vision-body">
            This is still unfolding. We don't have all the answers. But we have a
            growing group of people who have done the inner work — and a shared
            conviction that what emerges when they come together matters far beyond
            themselves.
          </p>
        </div>
      </motion.section>

      {/* ── WHO IT'S FOR ── */}
      <motion.section
        className="cm-who"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div className="cm-who-inner">
          <p className="section-label">Who this is for</p>
          <h2 className="section-title">
            The community requires the journey — and that's the point.
          </h2>

          <div className="cm-who-grid">
            <div className="cm-who-card">
              <div className="cm-who-card-label">Flow Journey alumni</div>
              <div className="cm-who-card-title">
                You've done the inner work.<br />This is what comes next.
              </div>
              <p className="cm-who-card-body">
                You completed the Flow Journey. You know what it means to move from
                survival patterns toward a Flow Identity. The SI Flow Community is
                the space where that awareness deepens — not in six weeks, but over
                a lifetime.{" "}
                <strong>
                  Where the real integration happens. Where the ripples go further.
                </strong>
              </p>
            </div>
            <div className="cm-who-card right">
              <div className="cm-who-card-label">Prospective Flow Journey participants</div>
              <div className="cm-who-card-title">
                See what you're working toward — before you begin.
              </div>
              <p className="cm-who-card-body">
                The Flow Journey isn't the destination. It's the preparation. The SI
                Flow Community is where Flow Journey alumni continue growing —
                together, in dialogue, in co-creation.{" "}
                <strong>
                  Understanding what's on the other side is part of what makes the
                  six weeks so worth it.
                </strong>
              </p>
            </div>
          </div>

          <div className="cm-who-gate">
            <span className="cm-who-gate-icon">🌊</span>
            <div className="cm-who-gate-text">
              <h4>Membership requires completing the Flow Journey</h4>
              <p>
                The quality of what's possible here depends on everyone in the space
                having done the inner work first. This isn't gatekeeping — it's the
                condition that makes genuine co-creation possible.{" "}
                <Link to="/flow-journey">Learn about the Flow Journey →</Link>
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── WHAT'S INSIDE ── */}
      <motion.section
        className="cm-inside"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={fadeUp}
      >
        <p className="section-label">Inside the community</p>
        <h2 className="section-title">What membership includes.</h2>
        <p className="cm-inside-intro">
          The SI Flow Community lives on Circle — the same platform where the Flow
          Journey runs. Everything is designed for depth, not volume. Real dialogue.
          Real practice. Real integration over time.
        </p>

        <div className="cm-inside-grid">
          {insideCards.map(({ icon, name, desc }) => (
            <div className="cm-inside-card" key={name}>
              <div className="cm-inside-wave">{icon}</div>
              <div className="cm-inside-name">{name}</div>
              <p className="cm-inside-desc">{desc}</p>
            </div>
          ))}
        </div>

        <div className="cm-inside-cocreate">
          <span className="cm-inside-cocreate-icon">🌱</span>
          <div className="cm-inside-cocreate-text">
            <h4>This community is co-created — not delivered</h4>
            <p>
              From the beginning, the Flow Journey has been built with participants,
              not just for them. The SI Flow Community extends that ethos. Members
              shape what the space becomes — through their presence, their ideas, and
              their willingness to show up for each other and for something larger.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── CO-CREATION ── */}
      <motion.section
        className="cm-cocreate"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div className="cm-cocreate-inner">
          <p className="section-label">The deeper purpose</p>
          <h2 className="section-title">
            This is where Synergistic Intelligence actually happens.
          </h2>
          <p className="cm-cocreate-body">
            The name isn't metaphorical. Synergistic Intelligence is what emerges
            when people operating from genuine awareness — rather than fear, ego, or
            survival patterns — think, create, and act together.{" "}
            <strong>It's a qualitatively different kind of intelligence.</strong> And
            we believe it's what the world's most complex problems actually require.
          </p>
          <p className="cm-cocreate-body">
            The SI Flow Community is the living laboratory for that hypothesis. Every
            dialogue, every prompt, every self-organized group is a small experiment
            in what becomes possible when the inner work meets collective action.
          </p>

          <div className="cm-cocreate-examples">
            {cocreateExamples.map(({ icon, label, desc }) => (
              <div className="cm-cocreate-ex" key={label}>
                <div className="cm-cocreate-ex-icon">{icon}</div>
                <div className="cm-cocreate-ex-text">
                  <strong>{label}</strong>
                  {desc}
                </div>
              </div>
            ))}
          </div>

          <p className="cm-cocreate-body">
            We're early. We're learning. We're building this with the humility of
            people who know they don't have all the answers — and the conviction of
            people who believe this direction is right.
          </p>
        </div>
      </motion.section>

      {/* ── PRICING ── */}
      <motion.section
        className="cm-pricing"
        id="pricing"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div className="cm-pricing-inner">
          <p className="cm-pricing-label">Membership</p>
          <h2 className="cm-pricing-title">Join the SI Flow Community.</h2>

          <div className="cm-pricing-card">
            <div className="cm-pricing-amount">
              $29 <span>/ month</span>
            </div>
            <div className="cm-pricing-founding">
              Founding Member Rate · Available to Flow Journey alumni
            </div>
            <div className="cm-pricing-includes">
              <h4>Everything included</h4>
              {pricingItems.map((item) => (
                <div className="cm-pricing-item" key={item}>
                  <span className="cm-pricing-check">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="cm-pricing-cta-group">
              <a href="https://circle.so/YOUR_COMMUNITY_LINK" target="_blank" rel="noreferrer" className="btn-light-cm">
                Join the community →
              </a>
              <Link to="/flow-journey" className="btn-ghost-light-cm">
                Start with the Flow Journey first
              </Link>
            </div>
            <p className="cm-pricing-note">
              Membership requires completing the Flow Journey. Not there yet? The
              Flow Journey is your first step — and the community is part of what
              you're working toward.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── FOR FJ ALUMNI ── */}
      <motion.section
        className="cm-alumni"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
      >
        <div className="cm-alumni-inner">
          <p className="section-label">A note for Flow Journey alumni</p>
          <h2 className="section-title">
            You already know what this feels like.<br />
            <em>This is what comes next.</em>
          </h2>
          <p className="cm-alumni-body">
            You did the work. You know the difference between operating from fear and
            operating from flow — not as a concept, but as a felt experience. That
            awareness doesn't stabilize in six weeks. It deepens over time, in
            community, in practice.
          </p>
          <p className="cm-alumni-body">
            The SI Flow Community was built for you — and honestly,{" "}
            <strong>with you in mind from the beginning.</strong> The co-creation
            ethos that ran through your Flow Journey cohort continues here. Your
            experience, your insights, and your willingness to show up for something
            larger than yourself are exactly what this space needs to become what
            it's meant to be.
          </p>
          <div className="cm-alumni-pull">
            <p>
              "This is about more than staying in touch. It's about co-creating
              something the world needs — together."
            </p>
          </div>
          <p className="cm-alumni-body">
            Watch your inbox for everything you need to join. And if this lands for
            you — you already know what to do. 🌊
          </p>
          <div className="cm-alumni-cta-group">
            <a href="https://circle.so/YOUR_COMMUNITY_LINK" target="_blank" rel="noreferrer" className="btn-primary-cm">
              Join the community →
            </a>
            <a href="mailto:alec@synergisticintelligence.com" className="btn-ghost-cm">
              Ask us a question
            </a>
          </div>
        </div>
      </motion.section>

      {/* ── NOT YET ALUMNI ── */}
      <motion.section
        className="cm-not-yet"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={fadeUp}
      >
        <div className="cm-not-yet-inner">
          <p className="section-label" style={{ justifyContent: "center" }}>
            The first step
          </p>
          <h2>
            The community begins<br />with the Flow Journey.
          </h2>
          <p>
            The SI Flow Community is open to Flow Journey alumni. If you haven't
            done the journey yet — that's where this starts. Six weeks. A small
            group. The inner work that makes everything else possible.
          </p>
          <div className="cm-not-yet-cta-group">
            <Link to="/assessment" className="btn-primary-cm">
              Take the Flow Identity Assessment →
            </Link>
            <Link to="/flow-journey" className="btn-ghost-cm">
              Learn about the Flow Journey
            </Link>
          </div>
          <p className="cm-not-yet-note">
            Free · 10 minutes · See where you are on the journey
          </p>
        </div>
      </motion.section>
    </>
  );
}
